// utils.js
import React from 'react';
import { isValidEventType, getDefaultEventType, EXPERIENCE_LEVELS, TERRAIN_TYPES } from './constants/eventTypes';
import { VEHICLE_TYPES } from './constants/vehicleTypes';
import TerrainRoadIcon from './components/Icons/TerrainRoadIcon';
import TerrainOffroadIcon from './components/Icons/TerrainOffroadIcon';
import TerrainMixedIcon from './components/Icons/TerrainMixedIcon';
import MeetupIcon from './components/Icons/MeetupIcon';
import CompetitionIcon from './components/Icons/CompetitionIcon';
import RaceIcon from './components/Icons/RaceIcon';
import AdventureIcon from './components/Icons/AdventureIcon';
import TripIcon from './components/Icons/TripIcon';
import GatheringIcon from './components/Icons/GatheringIcon';
import CourseIcon from './components/Icons/CourseIcon';
import RideIcon from './components/Icons/RideIcon';
import ExhibitionIcon from './components/Icons/ExhibitionIcon';
import ExperienceBeginnerIcon from './components/Icons/ExperienceBeginnerIcon';
import ExperienceIntermediateIcon from './components/Icons/ExperienceIntermediateIcon';
import ExperienceAdvancedIcon from './components/Icons/ExperienceAdvancedIcon';
import MotorcycleIcon from './components/Icons/Vehicles/MotorcycleIcon';
import CarIcon from './components/Icons/Vehicles/CarIcon';
import AtvIcon from './components/Icons/Vehicles/AtvIcon';
import BuggyIcon from './components/Icons/Vehicles/BuggyIcon';
import FourX4Icon from './components/Icons/Vehicles/4x4Icon';
import GoKartIcon from './components/Icons/Vehicles/GoKartIcon';
import OtherVehicleIcon from './components/Icons/Vehicles/OtherVehicleIcon';
import AllVehiclesIcon from './components/Icons/Vehicles/AllVehicles';


export const getEventTypeSvgIcon = (eventType, color = '#989898') => {
  switch (eventType) {
    case 'meetup':
      return <MeetupIcon fill={color} />
    case 'competition':
      return <CompetitionIcon fill={color} />
    case 'race':
      return <RaceIcon fill={color} />
    case 'adventure':
      return <AdventureIcon fill={color} />
    case 'trip':
      return <TripIcon fill={color} />
    case 'gathering':
      return <GatheringIcon fill={color} />
    case 'course':
      return <CourseIcon fill={color} />
    case 'ride':
      return <RideIcon fill={color} />
    case 'exhibition':
      return <ExhibitionIcon fill={color} />
    default:
      return <MeetupIcon fill={color} />
  }
}

export const getEventExperienceSvgIcon = (experience, color) => {
  switch (experience) {
    case EXPERIENCE_LEVELS[0]: // 'beginner'
      return <ExperienceBeginnerIcon fill={color} />;
    case EXPERIENCE_LEVELS[1]: // 'intermediate'
      return <ExperienceIntermediateIcon fill={color} />;
    case EXPERIENCE_LEVELS[2]: // 'advanced'
      return <ExperienceAdvancedIcon fill={color} />;
    default:
      return <ExperienceBeginnerIcon fill={color} />;
  }
};

export const getEventTerrainSvgIcon = (terrain, color) => {
  switch (terrain) {
    case TERRAIN_TYPES[0]: // 'road'
      return <TerrainRoadIcon fill={color} />;
    case TERRAIN_TYPES[1]: // 'offroad'
      return <TerrainOffroadIcon fill={color} />;
    case TERRAIN_TYPES[2]: // 'all'
      return <TerrainMixedIcon fill={color} />;
    default:
      return <TerrainRoadIcon fill={color} />;
  }
}

export const getVehicleTypeSvgIcon = (vehicleType, color) => {
  switch (vehicleType) {
    case VEHICLE_TYPES.MOTORCYCLE:
      return <MotorcycleIcon fill={color} />
    case VEHICLE_TYPES.CAR:
      return <CarIcon fill={color} />
    case VEHICLE_TYPES.ATV:
      return <AtvIcon fill={color} />
    case VEHICLE_TYPES.BUGGY:
      return <BuggyIcon fill={color} />
    case VEHICLE_TYPES.GO_KART:
      return <GoKartIcon fill={color} />
    case 'all':
      return <AllVehiclesIcon fill={color} />
    default:
      return <OtherVehicleIcon fill={color} />
  }
}

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatTime = (dateString) => {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const formatDateTime = (dateString) => {
  return `${formatDate(dateString)} ${formatTime(dateString)}`;
};

export const formatPrice = (price) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const getInitials = (name) => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePassword = (password) => {
  return password.length >= 8;
};

export const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const throttle = (func, limit) => {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
};

export const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

export const getRandomColor = () => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text: ', err);
    return false;
  }
};

export const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'hace un momento';
  if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} minutos`;
  if (diffInSeconds < 86400) return `hace ${Math.floor(diffInSeconds / 3600)} horas`;
  if (diffInSeconds < 2592000) return `hace ${Math.floor(diffInSeconds / 86400)} días`;
  if (diffInSeconds < 31536000) return `hace ${Math.floor(diffInSeconds / 2592000)} meses`;
  return `hace ${Math.floor(diffInSeconds / 31536000)} años`;
};

export const sortByDate = (array, dateField, ascending = true) => {
  return array.sort((a, b) => {
    const dateA = new Date(a[dateField]);
    const dateB = new Date(b[dateField]);
    return ascending ? dateA - dateB : dateB - dateA;
  });
};

export const groupBy = (array, key) => {
  return array.reduce((result, currentValue) => {
    (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
    return result;
  }, {});
};

export const removeAccents = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const searchInArray = (array, searchTerm, fields) => {
  const normalizedSearchTerm = removeAccents(searchTerm.toLowerCase());
  return array.filter(item => {
    return fields.some(field => {
      const fieldValue = item[field];
      if (typeof fieldValue === 'string') {
        return removeAccents(fieldValue.toLowerCase()).includes(normalizedSearchTerm);
      }
      return false;
    });
  });
};

// export const getEventTypeColor = (eventType) => {
//   const colors = {
//     'race': '#FF6B6B',
//     'competition': '#4ECDC4',
//     'adventure': '#45B7D1',
//     'gathering': '#96CEB4',
//     'course': '#FFEAA7',
//     'exhibition': '#DDA0DD',
//     'meetup': '#98D8C8',
//     'ride': '#F7DC6F',
//     'trip': '#BB8FCE'
//   };
//   return colors[eventType] || '#85C1E9';
// };

// export const getTerrainColor = (terrain) => {
//   const colors = {
//     [TERRAIN_TYPES[0]]: '#2ECC71', // 'road'
//     [TERRAIN_TYPES[1]]: '#E67E22', // 'offroad'
//     'mixed': '#9B59B6',
//     'track': '#E74C3C'
//   };
//   return colors[terrain] || '#34495E';
// };

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radio de la Tierra en kilómetros
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const formatDistance = (distance) => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  }
  return `${distance.toFixed(1)} km`;
};

export const isEventInFuture = (eventDate) => {
  return new Date(eventDate) > new Date();
};

export const isEventToday = (eventDate) => {
  const today = new Date();
  const event = new Date(eventDate);
  return today.toDateString() === event.toDateString();
};

export const getEventStatus = (eventDate, endDate) => {
  const now = new Date();
  const start = new Date(eventDate);
  const end = endDate ? new Date(endDate) : start;

  if (now < start) return 'upcoming';
  if (now >= start && now <= end) return 'ongoing';
  return 'finished';
};

export const formatDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffInMs = end - start;
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffInHours === 0) {
    return `${diffInMinutes} min`;
  }
  if (diffInMinutes === 0) {
    return `${diffInHours} h`;
  }
  return `${diffInHours} h ${diffInMinutes} min`;
};

export const getAgeFromBirthDate = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

export const isValidPhoneNumber = (phone) => {
  const phoneRegex = /^[+]?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

export const formatPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 9) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  return phone;
};

export const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};

export const mergeObjects = (...objects) => {
  return objects.reduce((result, current) => {
    return { ...result, ...current };
  }, {});
};

export const pickProperties = (obj, properties) => {
  return properties.reduce((result, prop) => {
    if (obj.hasOwnProperty(prop)) {
      result[prop] = obj[prop];
    }
    return result;
  }, {});
};

export const omitProperties = (obj, properties) => {
  const result = { ...obj };
  properties.forEach(prop => {
    delete result[prop];
  });
  return result;
};

export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export const isEqual = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (let key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!isEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
};

export const retry = async (fn, maxAttempts = 3, delay = 1000) => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxAttempts) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
};

export const createQueryString = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value.toString());
    }
  });
  return searchParams.toString();
};

export const parseQueryString = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
};

export const downloadFile = (data, filename, type = 'text/plain') => {
  const blob = new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

export const compressImage = (file, quality = 0.8, maxWidth = 1920, maxHeight = 1080) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;
      const ratio = Math.min(maxWidth / width, maxHeight / height);

      canvas.width = width * ratio;
      canvas.height = height * ratio;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(resolve, 'image/jpeg', quality);
    };

    img.src = URL.createObjectURL(file);
  });
};

export const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

export const isImageFile = (file) => {
  return file.type.startsWith('image/');
};

export const isVideoFile = (file) => {
  return file.type.startsWith('video/');
};

export const isPdfFile = (file) => {
  return file.type === 'application/pdf';
};

export const getColorFromString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

export const lightenColor = (color, amount) => {
  const usePound = color[0] === '#';
  const col = usePound ? color.slice(1) : color;
  const num = parseInt(col, 16);
  let r = (num >> 16) + amount;
  let g = (num >> 8 & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  r = r > 255 ? 255 : r < 0 ? 0 : r;
  g = g > 255 ? 255 : g < 0 ? 0 : g;
  b = b > 255 ? 255 : b < 0 ? 0 : b;
  return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
};

export const darkenColor = (color, amount) => {
  return lightenColor(color, -amount);
};

export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

export const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

export const getContrastColor = (hexColor) => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return '#000000';
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

export const animateValue = (start, end, duration, callback) => {
  const startTime = performance.now();
  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const value = start + (end - start) * progress;
    callback(value);
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };
  requestAnimationFrame(animate);
};

export const easeInOut = (t) => {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
};

export const easeIn = (t) => {
  return t * t;
};

export const easeOut = (t) => {
  return t * (2 - t);
};

export const linear = (t) => {
  return t;
};

export const smoothScrollTo = (element, to, duration = 300) => {
  const start = element.scrollTop;
  const change = to - start;
  const startTime = performance.now();

  const animateScroll = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOut(progress);
    element.scrollTop = start + change * easedProgress;

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  };

  requestAnimationFrame(animateScroll);
};

export const getScrollPosition = () => {
  return {
    x: window.pageXOffset || document.documentElement.scrollLeft,
    y: window.pageYOffset || document.documentElement.scrollTop
  };
};

export const isElementInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const getElementOffset = (element) => {
  const rect = element.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return {
    top: rect.top + scrollTop,
    left: rect.left + scrollLeft
  };
};

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const isTablet = () => {
  return /iPad|Android(?!.*Mobile)/i.test(navigator.userAgent);
};

export const isDesktop = () => {
  return !isMobile() && !isTablet();
};

export const getDeviceType = () => {
  if (isMobile()) return 'mobile';
  if (isTablet()) return 'tablet';
  return 'desktop';
};

export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';

  if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  else if (ua.includes('Opera')) browser = 'Opera';

  return {
    name: browser,
    userAgent: ua,
    language: navigator.language,
    platform: navigator.platform
  };
};

export const getScreenInfo = () => {
  return {
    width: window.screen.width,
    height: window.screen.height,
    availWidth: window.screen.availWidth,
    availHeight: window.screen.availHeight,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth
  };
};

export const getViewportSize = () => {
  return {
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight
  };
};

export const onResize = (callback, delay = 250) => {
  let timeoutId;
  const debouncedCallback = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
  window.addEventListener('resize', debouncedCallback);
  return () => window.removeEventListener('resize', debouncedCallback);
};

export const onScroll = (callback, delay = 100) => {
  let timeoutId;
  const debouncedCallback = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
  window.addEventListener('scroll', debouncedCallback);
  return () => window.removeEventListener('scroll', debouncedCallback);
};

export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

export const preloadImages = (sources) => {
  return Promise.all(sources.map(preloadImage));
};

export const lazyLoad = (selector, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  };

  const config = { ...defaultOptions, ...options };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        observer.unobserve(img);
      }
    });
  }, config);

  document.querySelectorAll(selector).forEach(img => {
    observer.observe(img);
  });

  return observer;
};

export const createIntersectionObserver = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  };

  const config = { ...defaultOptions, ...options };
  return new IntersectionObserver(callback, config);
};

export const observeElement = (element, callback, options = {}) => {
  const observer = createIntersectionObserver(callback, options);
  observer.observe(element);
  return observer;
};

export const unobserveElement = (observer, element) => {
  observer.unobserve(element);
};

export const disconnectObserver = (observer) => {
  observer.disconnect();
};

export const createMutationObserver = (callback, options = {}) => {
  const defaultOptions = {
    childList: true,
    subtree: true,
    attributes: false,
    attributeOldValue: false,
    characterData: false,
    characterDataOldValue: false
  };

  const config = { ...defaultOptions, ...options };
  return new MutationObserver(callback, config);
};

export const observeMutations = (element, callback, options = {}) => {
  const observer = createMutationObserver(callback, options);
  observer.observe(element, options);
  return observer;
};

export const disconnectMutationObserver = (observer) => {
  observer.disconnect();
};

export const createResizeObserver = (callback) => {
  return new ResizeObserver(callback);
};

export const observeResize = (element, callback) => {
  const observer = createResizeObserver(callback);
  observer.observe(element);
  return observer;
};

export const unobserveResize = (observer, element) => {
  observer.unobserve(element);
};

export const disconnectResizeObserver = (observer) => {
  observer.disconnect();
};

export const createPerformanceObserver = (callback, options = {}) => {
  const defaultOptions = {
    entryTypes: ['measure', 'navigation', 'resource']
  };

  const config = { ...defaultOptions, ...options };
  const observer = new PerformanceObserver(callback);
  observer.observe(config);
  return observer;
};

export const measurePerformance = (name, fn) => {
  const startMark = `${name}-start`;
  const endMark = `${name}-end`;

  performance.mark(startMark);
  const result = fn();
  performance.mark(endMark);
  performance.measure(name, startMark, endMark);

  return result;
};

export const getPerformanceMetrics = () => {
  const navigation = performance.getEntriesByType('navigation')[0];
  return {
    domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
    loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
    domInteractive: navigation.domInteractive - navigation.navigationStart,
    firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime || 0,
    firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0
  };
};

export const clearPerformanceMarks = (name) => {
  if (name) {
    performance.clearMarks(name);
  } else {
    performance.clearMarks();
  }
};

export const clearPerformanceMeasures = (name) => {
  if (name) {
    performance.clearMeasures(name);
  } else {
    performance.clearMeasures();
  }
};

export const getMemoryUsage = () => {
  if (performance.memory) {
    return {
      used: performance.memory.usedJSHeapSize,
      total: performance.memory.totalJSHeapSize,
      limit: performance.memory.jsHeapSizeLimit
    };
  }
  return null;
};

export const logPerformance = (name, fn) => {
  const start = performance.now();
  const result = fn();
  const end = performance.now();
  console.log(`${name} took ${end - start} milliseconds`);
  return result;
};

export const createWorker = (fn) => {
  const blob = new Blob([`(${fn.toString()})()`], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

export const terminateWorker = (worker) => {
  worker.terminate();
};

export const postMessageToWorker = (worker, message) => {
  worker.postMessage(message);
};

export const onWorkerMessage = (worker, callback) => {
  worker.onmessage = (e) => callback(e.data);
};

export const onWorkerError = (worker, callback) => {
  worker.onerror = callback;
};

export const createSharedWorker = (scriptURL, name) => {
  return new SharedWorker(scriptURL, name);
};

export const createServiceWorker = (scriptURL) => {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.register(scriptURL);
  }
  return Promise.reject(new Error('Service Workers not supported'));
};

export const unregisterServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.getRegistrations().then(registrations => {
      return Promise.all(registrations.map(registration => registration.unregister()));
    });
  }
  return Promise.resolve();
};

export const getServiceWorkerRegistration = () => {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready;
  }
  return Promise.reject(new Error('Service Workers not supported'));
};

export const postMessageToServiceWorker = (message) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
};

export const onServiceWorkerMessage = (callback) => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (e) => callback(e.data));
  }
};

export const checkOnlineStatus = () => {
  return navigator.onLine;
};

export const onOnline = (callback) => {
  window.addEventListener('online', callback);
  return () => window.removeEventListener('online', callback);
};

export const onOffline = (callback) => {
  window.addEventListener('offline', callback);
  return () => window.removeEventListener('offline', callback);
};

export const getNetworkInformation = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection;
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData
    };
  }
  return null;
};

export const onNetworkChange = (callback) => {
  if ('connection' in navigator) {
    navigator.connection.addEventListener('change', callback);
    return () => navigator.connection.removeEventListener('change', callback);
  }
  return () => { };
};

export const getBatteryInfo = () => {
  if ('getBattery' in navigator) {
    return navigator.getBattery().then(battery => ({
      charging: battery.charging,
      level: battery.level,
      chargingTime: battery.chargingTime,
      dischargingTime: battery.dischargingTime
    }));
  }
  return Promise.resolve(null);
};

export const onBatteryChange = (callback) => {
  if ('getBattery' in navigator) {
    navigator.getBattery().then(battery => {
      battery.addEventListener('chargingchange', callback);
      battery.addEventListener('levelchange', callback);
      battery.addEventListener('chargingtimechange', callback);
      battery.addEventListener('dischargingtimechange', callback);
    });
  }
};

export const getGeolocation = (options = {}) => {
  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000
  };

  const config = { ...defaultOptions, ...options };

  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, config);
  });
};

export const watchGeolocation = (callback, errorCallback, options = {}) => {
  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000
  };

  const config = { ...defaultOptions, ...options };

  if (!navigator.geolocation) {
    errorCallback(new Error('Geolocation not supported'));
    return null;
  }

  return navigator.geolocation.watchPosition(callback, errorCallback, config);
};

export const clearGeolocationWatch = (watchId) => {
  if (navigator.geolocation && watchId) {
    navigator.geolocation.clearWatch(watchId);
  }
};

export const requestNotificationPermission = () => {
  if ('Notification' in window) {
    return Notification.requestPermission();
  }
  return Promise.reject(new Error('Notifications not supported'));
};

export const showNotification = (title, options = {}) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    return new Notification(title, options);
  }
  return null;
};

export const getNotificationPermission = () => {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'unsupported';
};

export const isNotificationSupported = () => {
  return 'Notification' in window;
};

export const requestPersistentStorage = () => {
  if ('storage' in navigator && 'persist' in navigator.storage) {
    return navigator.storage.persist();
  }
  return Promise.resolve(false);
};

export const getStorageEstimate = () => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    return navigator.storage.estimate();
  }
  return Promise.resolve(null);
};

export const isPersistentStorageGranted = () => {
  if ('storage' in navigator && 'persisted' in navigator.storage) {
    return navigator.storage.persisted();
  }
  return Promise.resolve(false);
};

export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error('Error setting localStorage:', error);
    return false;
  }
};

export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error getting localStorage:', error);
    return defaultValue;
  }
};

// Categorías de imágenes prediseñadas - fuente única de verdad
export const imageCategories = {
  meetup: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757949274/meetup-1_pm7jvw.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757949273/meetup-2_dqmbke.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757949271/meetup-3_r3d82q.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757949272/meetup-4_ztdbdx.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757949272/meetup-5_r3gdym.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757949271/meetup-6_zbrsr9.jpg'
  ],
  competition: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757946938/competition-1_tqmbpx.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757946938/competition-2_dinpwz.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757946938/competition-3_gppmtw.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757946938/competition-4_e9qgnx.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757946938/competition-5_n19fcw.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757946937/competition-6_zibqho.jpg'
  ],
  race: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757950233/race-1_j6e3pw.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757950234/race-2_naeeib.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757950236/race-3_g0ocxl.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757950236/race-4_ybtwkk.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757950232/race-5_qncvqp.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757950233/race-6_e8j9vg.jpg'
  ],
  adventure: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951147/adventure-1_jhavb0.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951145/adventure-2_jhwyid.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951144/adventure-3_kne7t5.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951142/adventure-4_hypu2l.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951142/adventure-5_vzhvvw.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951141/adventure-6_p74kaf.jpg'
  ],
  trip: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951634/trip-1_cmptsd.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951634/trip-2_coz2dj.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951635/trip-3_ettu8n.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951637/trip-4_kvgrzf.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951638/trip-5_txz2wp.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951633/trip-6_wfvb22.jpg'
  ],
  gathering: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951929/gathering-1_z1o9j0.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951928/gathering-2_gsamsz.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951926/gathering-3_i7np74.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951925/gathering-4_rphosv.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951924/gathering-5_eoykpv.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757951923/gathering-6_qazg78.jpg'
  ],
  course: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952778/course-1_lvhb5q.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952785/course-2_csb9ks.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952784/course-3_kzfrxs.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952782/course-4_jlxwhg.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952781/course-5_varta8.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952779/course-6_exhanp.jpg'
  ],
  ride: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952397/ride-1_v8h50d.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952396/ride-2_psjcoz.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952394/ride-3_pimp4r.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952393/ride-4_pzmemo.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952391/ride-5_jhpjrf.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757952391/ride-6_xkywim.jpg'
  ],
  exhibition: [
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757948752/exhibition-1_qs1gd3.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757948753/exhibition-2_jr2vga.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757948753/exhibition-3_xcnyvc.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757948753/exhibition-4_rsis8a.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757948753/exhibition-5_hk66fv.jpg',
    'https://res.cloudinary.com/dmzcnqai3/image/upload/v1757948752/exhibition-6_zrjvpo.jpg'
  ]
};

// Función para obtener una imagen aleatoria basada en el tipo de evento
export const getRandomImageForEventType = (eventType) => {
  // Validar que el tipo de evento sea válido
  const validEventType = isValidEventType(eventType) ? eventType : getDefaultEventType();

  // Obtener las imágenes para el tipo de evento específico
  const imagesForType = imageCategories[validEventType] || imageCategories.meetup;

  // Seleccionar una imagen aleatoria
  const randomIndex = Math.floor(Math.random() * imagesForType.length);
  return imagesForType[randomIndex];
};
